import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { FindingClaim, FindingEvidenceState } from '../../../../lib/findingClaim';
import { FindingClaimCard } from '../../report/FindingClaimCard';
import { SectionShell } from '../SectionShell';
import { walkClaimsText } from '../../../../lib/walkClaimsText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge, type EditorialBadgeTone } from '../../report/editorial';

interface Props {
  report: SiteRagReport;
  label: string;
}

/** Severity → EditorialBadge tone (replaces filled pill backgrounds). */
const SEV_TONE: Record<string, EditorialBadgeTone> = {
  high: 'negative',
  medium: 'warning',
  low: 'neutral',
};

function toFindingClaim(item: NonNullable<SiteRagReport['executiveSummaryCriticalRisks']>[number]): FindingClaim {
  return {
    claim: item.risk,
    evidenceState: (item.evidenceState ?? 'unknown') as FindingEvidenceState,
    ...(item.openQuestion ? { openQuestion: item.openQuestion } : {}),
    ...(item.evidence ? { evidence: item.evidence } : {}),
  };
}

export const CriticalRisksSection: React.FC<Props> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();
  const risks = report.executiveSummaryCriticalRisks ?? [];
  return (
    <SectionShell label={label} hasData={risks.length > 0} report={report} sectionId="criticalRisks">
      <ul className="list-none space-y-3 pl-0 m-0">
        {risks.map((item, i) => (
          <li key={i} className="text-sm leading-relaxed space-y-2">
            <div className="flex items-start gap-2">
              <span className="shrink-0 mt-0.5">
                <EditorialBadge
                  theme={theme}
                  tone={SEV_TONE[item.severity] ?? SEV_TONE.medium}
                  variant="outline"
                >
                  {item.severity}
                </EditorialBadge>
              </span>
              <FindingClaimCard claim={toFindingClaim(item)} references={report.references} theme={theme} />
            </div>
            {item.mitigation && (
              <p className="text-xs mt-1 mb-0 pl-0" style={{ color: roleColor(theme, 'muted') }}>
                Mitigation: {walkClaimsText(item.mitigation, report.references, report.claims)}
              </p>
            )}
          </li>
        ))}
      </ul>
    </SectionShell>
  );
};
